import React, { useState, useEffect, useCallback } from 'react';
import { ProfessionalLayout } from '@/modules/professional/layouts/ProfessionalLayout';
import { PaymentValidator } from '@/modules/professional/components/PaymentValidator';
import { PaymentQueueRepository } from '@/core/application/financial/PaymentQueueRepository';
import type { Comprobante, Decision } from '@/core/application/financial/PaymentQueueRepository';
import { RefreshCw, CheckCircle2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// ─── Pantalla Orquestadora (Liviana) ─────────────────────────────────────────

/**
 * ComprobantesQueueScreen — Orquestador de la cola de pagos.
 * 
 * Esta pantalla NO contiene lógica de negocio ni componentes visuales complejos.
 * Solo orquesta:
 *   1. PaymentQueueRepository (datos)
 *   2. PaymentValidator (visual)
 * 
 * Cumple Nota 16.1 (Anti-Fallo en Cascada): si mañana se añade un
 * <TransactionalChat /> aquí, será otro bloque independiente.
 */
export function ComprobantesQueueScreen() {
  const navigate = useNavigate();
  const [comprobantes, setComprobantes] = useState<Comprobante[]>([]);
  const [loading,      setLoading]      = useState(true);
  const [refreshing,   setRefreshing]   = useState(false);
  const [filtro,       setFiltro]       = useState<'pendientes' | 'aprobados' | 'incompletos' | 'rechazados' | 'todos'>('pendientes');

  const cargar = useCallback(async (esRefresh = false) => {
    if (esRefresh) setRefreshing(true); else setLoading(true);
    const datos = await PaymentQueueRepository.fetchComprobantes();
    setComprobantes(datos);
    setLoading(false);
    setRefreshing(false);
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const handleDecision = async (citaId: string, decision: Decision, nota?: string) => {
    const result = await PaymentQueueRepository.procesarDecision(citaId, decision, nota);
    if (result.success) {
      await cargar(true);
    } else {
      globalThis.alert(result.message ?? 'Error desconocido');
    }
  };

  const handleViewDetails = (citaId: string) => {
    navigate(`/pro/comprobantes/${citaId}`);
  };

  // ── Filtrado local ──
  const listaDatos = filtro === 'todos' 
    ? comprobantes 
    : comprobantes.filter(c => {
        if (filtro === 'pendientes') return c.status_pago === 'sin_verificar';
        if (filtro === 'aprobados') return c.status_pago === 'aprobado';
        if (filtro === 'incompletos') return c.status_pago === 'pago_parcial';
        if (filtro === 'rechazados') return c.status_pago === 'rechazado';
        return true;
      });

  const pendientesCount = comprobantes.filter(c => c.status_pago === 'sin_verificar').length;

  // ── Loading State ──
  if (loading) {
    return (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: '16px', backgroundColor: '#F8FAFC' }}>
        <div style={{ width: 40, height: 40, border: `4px solid #0052CC40`, borderTopColor: '#0052CC', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
        <span style={{ fontSize: '15px', color: '#64748B' }}>Cargando comprobantes...</span>
      </div>
    );
  }

  return (
    <ProfessionalLayout>
      <div style={{ flex: 1, backgroundColor: '#F8FAFC', display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
        {/* HEADER */}
        <header style={{
          display: 'flex', alignItems: 'center', backgroundColor: '#FFFFFF', padding: '14px 16px',
          borderBottom: '1px solid #F1F5F9'
        }}>
          <div style={{ flex: 1 }}>
            <h1 style={{ fontSize: '16px', fontWeight: 700, color: '#0A2540', margin: 0 }}>Comprobantes de pago</h1>
            {pendientesCount > 0 && (
              <p style={{ fontSize: '12px', color: '#F59E0B', fontWeight: 600, marginTop: '1px', marginBottom: 0 }}>
                {pendientesCount} pendiente{pendientesCount > 1 ? 's' : ''} de revisión
              </p>
            )}
          </div>
          <button 
            onClick={() => cargar(true)}
            style={{ width: '40px', height: '40px', borderRadius: '20px', backgroundColor: '#EFF6FF', border: 'none', display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'pointer' }}
          >
            <RefreshCw size={20} color="#0052CC" className={refreshing ? "spin-animation" : ""} />
          </button>
        </header>

        {/* FILTRO */}
        <div style={{ display: 'flex', padding: '12px 16px', backgroundColor: '#FFFFFF', borderBottom: '1px solid #F1F5F9', gap: '8px', overflowX: 'auto', whiteSpace: 'nowrap' }}>
          {(['pendientes', 'aprobados', 'incompletos', 'rechazados', 'todos'] as const).map(f => {
            const isPendientes = f === 'pendientes';
            let label = f.charAt(0).toUpperCase() + f.slice(1);
            if (isPendientes && pendientesCount > 0) {
              label = `Pendientes (${pendientesCount})`;
            }
            const isActivo = filtro === f;
            return (
              <button
                key={f}
                onClick={() => setFiltro(f)}
                style={{
                  padding: '8px 16px', borderRadius: '100px', border: 'none', cursor: 'pointer',
                  backgroundColor: isActivo ? '#0052CC' : '#F1F5F9',
                  color: isActivo ? '#FFFFFF' : '#64748B',
                  fontSize: '14px', fontWeight: 600
                }}
              >
                {label}
              </button>
            );
          })}
        </div>

        {/* LISTA — Bloque de Lego: PaymentValidator */}
        <div style={{ padding: '16px 16px 40px', overflowY: 'auto' }}>
          {listaDatos.length > 0 ? (
            <PaymentValidator 
              comprobantes={listaDatos} 
              onDecision={handleDecision} 
              onViewDetails={handleViewDetails} 
            />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: '80px', gap: '12px' }}>
              <CheckCircle2 size={56} color="#CBD5E1" />
              <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#0A2540', margin: 0 }}>
                {filtro === 'pendientes' ? '¡Todo al día!' : 'Lista vacía'}
              </h2>
              <p style={{ fontSize: '15px', color: '#94A3B8', textAlign: 'center', lineHeight: '22px', padding: '0 32px', margin: 0 }}>
                {filtro === 'pendientes'
                  ? 'No tienes comprobantes pendientes de revisión.'
                  : 'No hay comprobantes en la categoría seleccionada.'}
              </p>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin { 100% { transform: rotate(360deg); } }
        .spin-animation { animation: spin 1s linear infinite; }
      `}</style>
    </ProfessionalLayout>
  );
}
